import { useMachine } from '@xstate/react'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { createMachine } from 'xstate'
import { Header } from '../../components/Header'
import { getButtonTooltip, IntakeCard } from '../../components/Intake/IntakeCard'
import { Modal } from '../../components/Modal'
import { SelectOptions, UnstyledSelect } from '../../components/Select'
import { SubHeading } from '../../components/SubHeading'
import { ApplicationData } from '../../contexts/AppContext'
import { AddButton } from '../../elements/AddButton'
import { Button } from '../../elements/Button'
import { CustomLink } from '../../elements/CustomLink'
import { Error404 } from '../../elements/Error404'
import { ErrorLabel } from '../../elements/ErrorLabel'
import { Heading } from '../../elements/Heading'
import { useAppData } from '../../hooks/useAppData'
import { useQuery } from '../../hooks/useQuery'
import { fetchCourses, selectCourseOptionsForAdditionalCourses } from '../../redux/courseSlice'
import { fetchIntake, selectApplicationTypeOptions, selectIntakeById } from '../../redux/intakeSlice'
import { LoadingStates, RootState, useAppSelector } from '../../types/assets'
import { GlobalStyles } from '../Home/styles'
import { AccessCodeValidator } from './AccessCodeValidator'
import { AddAdditionalCoursesModal } from './AddAdditionalCoursesModal'
import { useIntakeContext } from './IntakeContext'
import { IntakeSkeleton } from './IntakeSkeleton'
import { ApplicationTypeOptionsRow, StyledIntakeContainer } from './styles'
import { WhiteListedDomainsValidator } from './WhiteListedDomainsValidator'

dayjs.extend(isBetween)

export type IntakeProps = {}

export const IntakePage = (props: any) => {
  let { id } = useParams()
  const query = useQuery()

  const dispatch = useDispatch()
  const navigate = useNavigate()

  type IntakeEvents = {
    type: 'LOAD'
    payload: { hasIntake: boolean; status: LoadingStates }
  }

  const intakeMachine = useMemo(() => {
    return createMachine<undefined, IntakeEvents>({
      id: 'intake',
      initial: 'birth',
      states: {
        birth: {
          on: {
            LOAD: [
              {
                target: 'loading',
                cond: (context, event) => !event.payload.hasIntake && event.payload.status !== 'failed',
              },
              {
                target: 'idle',
                cond: (context, event) => event.payload.hasIntake,
              },
            ],
          },
        },
        loading: {
          on: {
            LOAD: [
              {
                target: 'idle',
                cond: (context, event) => event.payload.hasIntake,
              },
              {
                target: 'error',
                cond: (context, event) => event.payload.status === 'failed',
              },
            ],
          },
          entry: () => {
            dispatch(fetchIntake(id as string))
          },
        },
        idle: {
          type: 'final',
        },
        error: {
          type: 'final',
        },
      },
    })
  }, [dispatch, id])

  const [machine, send] = useMachine(intakeMachine)

  const intake = useAppSelector(state => selectIntakeById(state, id as string))

  const applicationTypes = useAppSelector(state => selectApplicationTypeOptions(state, id as string))

  const applicationLinkGuid = query?.get('applicationLinkGuid')

  const {
    settings: {
      config: { path, siteStatus, allowCourses },
    },
    courses: {
      loading: { all },
    },
    intakes: { loading: status },
  } = useAppSelector(state => state)

  const [applicationTypeId, setApplicationTypeId] = useState<number>()

  const [showTestModal, setShowTestModal] = useState(false)

  const [accessCodeStatus, setAccessCodeStatus] = useState<'idle' | 'verified' | 'unVerified'>('idle')

  const [emailStatus, setEmailStatus] = useState<'idle' | 'verified' | 'unVerified'>('idle')

  const { additionalIntakes, additionalIntakesCount, removeAdditionalCourse, selectedCourseIds } = useIntakeContext()

  useEffect(() => {
    send('LOAD', { payload: { hasIntake: !!intake, status } })
  }, [intake, send, status])

  useEffect(() => {
    if (all !== 'succeeded') {
      dispatch(fetchCourses())
    }
  }, [])

  const applicationType = React.useMemo(
    () => intake?.salesIntakeApplicationTypes?.find(appType => appType?.applicationTypeId === applicationTypeId),
    [applicationTypeId, intake?.salesIntakeApplicationTypes],
  )

  const disableButton: boolean =
    !applicationTypeId ||
    (applicationType?.accessCodeFlag && accessCodeStatus !== 'verified') ||
    (applicationType?.accessWhitelistDomainsFlag && emailStatus !== 'verified') ||
    !dayjs().isBetween(
      intake?.applicationAllowStartDate ?? dayjs().subtract(1, 'days'),
      intake?.applicationAllowEndDate ?? dayjs().add(1, 'days'),
    ) ||
    intake?.vacancyCount! <= 0

  const appData: ApplicationData = {
    intakeGuid: intake?.intakeGuid,
    intakeName: intake?.publishName!,
    courseOfferId: intake?.courseOfferId!,
    applicationTypeId: applicationTypeId,
    formGuid: applicationType?.formGuid,
    applicationType: applicationType?.applicationTypeName!,
    paymentRequired:
      applicationType?.paymentType === 'Deposit Payment on Application' ||
      applicationType?.paymentType === 'Full Payment on Application',
    applicationLinkGuid: query.get('applicationLinkGuid')!,
    opportunityId: query.get('opportunityId')!,
    additionalIntakes: additionalIntakes?.map(i => ({
      additionalIntakeGuid: i.intakeGuid || '',
      additionalIntakeName: i.publishName || '',
    })),
  }

  const { updateAppData } = useAppData()
  const showViewMoreSection = !applicationLinkGuid && allowCourses && intake?.publishType === 'Public'

  const onStartApplicationClick = () => {
    updateAppData?.(appData)
    if (disableButton) {
      return
    } else if (siteStatus === 'Test') {
      setShowTestModal(true)
    } else {
      navigate(`/${path}/application/${applicationType?.formGuid}`)
    }
  }

  const onModalOkClick = () => {
    setShowTestModal(false)
    navigate(`/${path}/application/${applicationType?.formGuid}`)
  }

  React.useEffect(() => {
    if (applicationTypes?.length! === 1) {
      setApplicationTypeId(applicationTypes?.[0]?.value!)
    }
  }, [applicationTypes])

  const buttonTooltip = useMemo(() => {
    if (disableButton) {
      if (!!applicationTypeId) {
        if (applicationType?.accessCodeFlag && accessCodeStatus !== 'verified') {
          if (accessCodeStatus === 'idle') {
            return 'Start Application Unavailable - Access code is needed'
          }
          return 'Start Application Unavailable - Access code is incorrect'
        }

        if (applicationType?.accessWhitelistDomainsFlag && emailStatus !== 'verified') {
          if (emailStatus === 'idle') {
            return 'Start Application Unavailable - Email address is needed'
          }
          return 'Start Application Unavailable - Email address is incorrect'
        }

        return getButtonTooltip(intake, 'Start Application')
      } else {
        return 'Please Select an Application Type'
      }
    }
  }, [
    accessCodeStatus,
    emailStatus,
    applicationType?.accessCodeFlag,
    applicationType?.accessWhitelistDomainsFlag,
    applicationTypeId,
    disableButton,
    intake,
  ])

  const applicationTypeOptions: SelectOptions =
    applicationTypes?.map(appType => ({
      value: `${appType?.value}` || '',
      label: `${appType?.label}` || '',
    })) || []

  const numberOfCoursesToShowOnDropdown = useSelector(
    (state: RootState) => selectCourseOptionsForAdditionalCourses(state, id!, selectedCourseIds).length,
  )
  const showAdditionalCourses =
    (!!intake?.salesIntakeAllowedCourses?.length && !!numberOfCoursesToShowOnDropdown) || !!additionalIntakesCount

  const [showAddCoursesModal, setShowAddCoursesModal] = useState(false)

  const toggleAddCourseModal = () => {
    setShowAddCoursesModal(!showAddCoursesModal)
  }

  const setSelectedApplicationType = (value: string) => {
    setApplicationTypeId(+value)
  }

  const showPaymentAmount =
    (applicationType?.paymentType === 'Deposit Payment on Application' ||
      applicationType?.paymentType === 'Full Payment on Application') &&
    applicationType?.paymentAmount &&
    applicationType?.paymentAmount > 0 &&
    (!applicationType?.accessCodeFlag || accessCodeStatus === 'verified')

  const addButtonTooltip = additionalIntakesCount >= 5 ? 'You can only add upto 5 additional courses' : undefined

  return (
    <StyledIntakeContainer>
      <GlobalStyles greyColor />
      {machine.value === 'loading' ? (
        <IntakeSkeleton />
      ) : status === 'failed' ? (
        <Error404 />
      ) : machine.value === 'idle' ? (
        <>
          <Header title=" Before you start here is a summary of your course selection" />
          <IntakeCard
            intake={intake}
            type="detailed"
          />
          <div className="select-section">
            <>
              {showAdditionalCourses && (
                <>
                  <SubHeading subHeading="Additional Courses">
                    <AddButton
                      label="Add Courses"
                      onClick={toggleAddCourseModal}
                      disabled={additionalIntakesCount >= 5}
                      tooltip={addButtonTooltip}
                    />
                  </SubHeading>
                  <AddAdditionalCoursesModal
                    showModal={showAddCoursesModal}
                    onClose={toggleAddCourseModal}
                    intakeId={id || ''}
                  />
                  <>
                    {additionalIntakes.map(i => (
                      <IntakeCard
                        intake={i}
                        type="compact"
                        onRemove={removeAdditionalCourse}
                      />
                    ))}
                  </>
                </>
              )}

              <SubHeading subHeading="Application Details" />
              {applicationTypes?.length! > 1 && (
                <ApplicationTypeOptionsRow>
                  <UnstyledSelect
                    label="Application Type"
                    placeholder="Please select an appropriate application type"
                    onChange={setSelectedApplicationType}
                    options={applicationTypeOptions}
                  />
                </ApplicationTypeOptionsRow>
              )}
            </>

            {applicationTypes?.length! < 1 && (
              <label>
                There are no available ApplicationTypes setup. Unable to proceed. Please choose another Intake or
                contact the provider
              </label>
            )}

            {applicationTypeId && (
              <>
                <ApplicationTypeOptionsRow>
                  <span className="name">Payment Requirements: </span>
                  <span> {applicationType?.paymentType} </span>
                </ApplicationTypeOptionsRow>

                {applicationType?.accessCodeFlag && (
                  <AccessCodeValidator
                    key={applicationTypeId}
                    guid={id}
                    applicationTypeId={applicationTypeId.toString()}
                    onVerify={setAccessCodeStatus}
                    verified={accessCodeStatus === 'verified'}
                  />
                )}

                {applicationType?.accessWhitelistDomainsFlag && (
                  <WhiteListedDomainsValidator
                    key={applicationTypeId}
                    guid={id}
                    applicationTypeId={applicationTypeId.toString()}
                    onVerify={setEmailStatus}
                    verified={emailStatus === 'verified'}
                  />
                )}

                {showPaymentAmount && (
                  <ApplicationTypeOptionsRow>
                    <span className="name">{`Payment Amount (Incl Tax):`} </span>
                    <span>{`${applicationType?.currencySymbol || ''}${
                      applicationType?.paymentAmountIncludingTax
                    }`}</span>
                  </ApplicationTypeOptionsRow>
                )}
              </>
            )}
          </div>
          <div className="button-section">
            <Button
              large
              disabled={disableButton}
              tooltip={buttonTooltip}
              label="START APPLICATION"
              onClick={onStartApplicationClick}
            />
            {intake?.vacancyCount! <= 0 && <ErrorLabel>No Spots Available</ErrorLabel>}

            <Modal
              showModal={showTestModal}
              onClose={() => setShowTestModal(false)}
            >
              <Heading size="M">THIS SITE IS IN TEST MODE - FOR TESTING ONLY</Heading>
              <Button
                onClick={onModalOkClick}
                label="OK"
              />
            </Modal>
          </div>

          {showViewMoreSection && (
            <div className="view-more">
              <span>or </span>
              <CustomLink
                to={`/${path}/courses/${intake?.courseGuid}`}
                label="View More Availability"
              />
            </div>
          )}
        </>
      ) : null}
    </StyledIntakeContainer>
  )
}
